// import skaler from 'skaler';

// Security: Blocked file types that could contain malicious code
export const BLOCKED_FILE_TYPES = [
	'image/svg+xml', // SVG files can contain JavaScript
	'text/html', // HTML files
	'text/javascript', // JavaScript files
	'application/javascript', // JavaScript files
	'text/xml', // XML files
	'application/xml', // XML files
	'text/plain', // Plain text (could contain scripts)
	'application/x-shockwave-flash', // Flash files
	'application/x-executable', // Executable files
	'application/octet-stream' // Binary files
];

// Allowed image types (safe formats only)
export const ALLOWED_IMAGE_TYPES = [
	'image/jpeg',
	'image/jpg',
	'image/png',
	'image/webp',
	'image/avif',
	'image/gif' // Note: GIFs are generally safe but can be large
];

// Maximum file sizes in MB
export const MAX_FILE_SIZES = {
	AVATAR: 2,
	POST_IMAGE: 5,
	LOCATION_IMAGE: 5,
	OFFER_IMAGE: 5,
	DEFAULT: 5
};

/**
 * Comprehensive file validation that blocks dangerous file types
 * @param file The file to validate
 * @param maxSizeMB Maximum file size in MB
 * @param allowedTypes Optional array of allowed MIME types (defaults to ALLOWED_IMAGE_TYPES)
 * @returns Object with validation result and error message
 */
export const validateFile = (
	file: File,
	maxSizeMB: number = MAX_FILE_SIZES.DEFAULT,
	allowedTypes: string[] = ALLOWED_IMAGE_TYPES
): { isValid: boolean; error?: string } => {
	// Check if file is blocked due to security concerns
	if (BLOCKED_FILE_TYPES.includes(file.type)) {
		return {
			isValid: false,
			error: `File type '${file.type}' is not allowed for security reasons. Please use a different file.`
		};
	}

	// Check if file type is in allowed list
	if (!allowedTypes.includes(file.type)) {
		return {
			isValid: false,
			error: `File type '${file.type}' is not supported. Please use: ${allowedTypes.join(', ')}`
		};
	}

	// Check file size
	const fileSizeMB = file.size / 1024 / 1024;
	if (fileSizeMB > maxSizeMB) {
		return {
			isValid: false,
			error: `File size (${fileSizeMB.toFixed(1)}MB) exceeds the maximum allowed size of ${maxSizeMB}MB.`
		};
	}

	// Additional security checks
	if (file.name.toLowerCase().includes('.svg')) {
		return {
			isValid: false,
			error: 'SVG files are not allowed for security reasons. Please use JPEG, PNG, or WebP format.'
		};
	}

	// Check for suspicious file extensions
	const suspiciousExtensions = [
		'.svg',
		'.html',
		'.htm',
		'.js',
		'.xml',
		'.txt',
		'.exe',
		'.bat',
		'.sh'
	];
	const fileName = file.name.toLowerCase();
	if (suspiciousExtensions.some((ext) => fileName.endsWith(ext))) {
		return {
			isValid: false,
			error: 'This file type is not allowed for security reasons. Please use a different file.'
		};
	}

	return { isValid: true };
};

/*
 * validateSize:  Validates size of input files
 * @param <File>file - the HTML input we're receiving the file from
 * @return
 *
 * for more info see: https://stackoverflow.com/a/44505315
 */
export const validateFileSize = (file: File, maxSize: number): boolean => {
	const fileSize = file.size / 1024 / 1024; // in MiB
	if (fileSize > maxSize) {
		return false;
	} else {
		// console.log('File size ' + fileSize + ' is within ' + fileSize);
		return true;
	}
};

/*
 * scaleFileSize:  Scales size of input files - see $lib/constants.ts
 * @param <File>file - the file to scale
 * @return <File>file - the scaled file
 *
 */
export const scaleFileSize = async (
	file: File
	// height: number,
	// width: number,
	// scale_uploads: boolean
): Promise<File> => {
	return file;
	// if (scale_uploads) {
	// 	return await skaler(file, { width, height });
	// } else {
	// 	return file;
	// }
};
